import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { ManualProcessService } from '@features/contratacion/services/manual-process.service';
import { LoadingBackdropService } from '@shared/services/loading-backdrop.service';

/**
 * Component that handles the execution of external processes.
 * It processes URL query parameters and redirects to the appropriate view based on the parameters.
 */
@Component({
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
  ],
  template: `<router-outlet/>`,
  styles: [
    `
      :host {
        background-color: #fff;
      }
    `,
  ],
})
export class ExecuteExternalProcessComponent implements OnInit {

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly manualProcessService: ManualProcessService,
    private readonly loadingService: LoadingBackdropService,
  ) { }
  
  ngOnInit(): void {
    this.loadingService.show();
    const { uuid, query_params } = this.route.snapshot.queryParams;
    if (uuid) {
      this.queryParams(uuid);
    } else if (query_params) {
      this.processFormWithParams(query_params);
    } else {
      this.navigateToFinishView(null);
    }
  }

  /**
   * Queries the backend for query parameters using the provided UUID.
   * On success, processes the form with the retrieved parameters.
   * On error, navigates to the finish view with the error status.
   */
  private queryParams(uuid: string) {
    this.manualProcessService.getQueryParams(uuid).subscribe({
      next: (resp) => { this.processFormWithParams(resp.query_string) },
      error: (error) => {
        this.navigateToFinishView(error.status);
      }
    }).add(() => this.loadingService.hide());
  }

  /**
   * Processes the form with the provided query parameters.
   * Shows a loading indicator while processing and makes a service call to get the next step.
   * @param queryParams - The query parameters string to process
   */
  private processFormWithParams(queryParams: string): void {
    this.manualProcessService.getProcedureFormNextStep(queryParams, true, true).subscribe({
      next: (_resp) => this.navigateToFormulario(queryParams),
      error: (error) => this.navigateToFinishView(error.status)
    }).add(() => this.loadingService.hide());
  }

  /**
   * Navigates to the form view with the provided query parameters.
   * @param queryParams - The query parameters to pass as state
   */
  private navigateToFormulario(queryParams: string): void {
    this.router.navigate(['formulario'], { relativeTo: this.route, state: { queryParam: queryParams } });
  }
  
  /**
   * Navigates to the finish view with an optional status code.
   * @param state - The status code to pass as state, can be null
   */
  private navigateToFinishView(state: number | null): void {
    this.loadingService.hide()
    this.router.navigate(['finish'], { relativeTo: this.route, state: { status: state } });
  }
}